import { useEffect, useRef, useState } from 'react';

const BASE_OFFSET_PX = 24;
const DOCK_GAP_PX = 12;

/**
 * Observe les éléments fixed-bottom marqués `[data-floating-dock]` et
 * calcule un offset bottom en pixels pour empiler le bouton flottant
 * (chat AI) au-dessus sans collision.
 *
 * Les docks s'empilent verticalement (pattern PHASES.md : barres d'action
 * sticky AU-DESSUS du BottomTabBar), donc l'offset additionne leurs hauteurs —
 * prendre seulement le plus haut ferait chevaucher le FAB avec la barre du dessus.
 *
 * Re-run sur mutations DOM + resize. Retourne `BASE_OFFSET_PX` si rien.
 *
 * Visibilité : on filtre via `getBoundingClientRect()` plutôt que `offsetParent`
 * car `HTMLElement.offsetParent` retourne `null` pour les éléments `position: fixed`
 * (cf. spec CSSOM / MDN), ce qui ferait écarter à tort tous les docks fixes.
 */
export function useFloatingDockOffset(base: number = BASE_OFFSET_PX): number {
  const [offset, setOffset] = useState(base);
  const lastRef = useRef(base);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const compute = () => {
      const heights = Array.from(document.querySelectorAll<HTMLElement>('[data-floating-dock]'))
        .map((el) => el.getBoundingClientRect().height)
        .filter((h) => h > 0);

      const next =
        heights.length === 0
          ? base
          : base + heights.reduce((sum, h) => sum + h, 0) + DOCK_GAP_PX;

      if (next === lastRef.current) return;
      lastRef.current = next;
      setOffset(next);
    };

    compute();

    const observer = new MutationObserver(compute);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    window.addEventListener('resize', compute);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [base]);

  return offset;
}
